import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import SharedReactTable from "components/shared/reactTable";
import { dateTimeToView } from "helpers/date";
import { paths } from "helpers/paths";
import { BooleanColumnFilter, DateRangeColumnFilter, FilterProps, rowIndexColumn } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { generateIntegrationClientSecret } from "helpers/secureRandom";
import React, { FC } from "react";
import { Link } from "react-router-dom";
import { alert } from "services/alert";
import { applyIntegrationClient, IntegrationClientListModel } from "sources/admin/integrationClients";
import SecretModal from "./secretModal";

class IntegrationClientsList extends BookIndex {
    componentWillUnmount() {
        this.props.clearParams();
    }

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "Название",
                accessor: "name",
                filterable: true,
                Cell: ({ value, row }: { value: string; row: { original: IntegrationClientListModel } }) => (
                    <Link to={paths.integrationClient(encodeURIComponent(row.original.clientId))}>{value}</Link>
                ),
            },
            {
                Header: "Client ID",
                accessor: "clientId",
                filterable: true,
                Cell: ({ value }: { value: string }) => (
                    <div>
                        <code className="text-break mr-2">{value}</code>
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-primary"
                            onClick={() => this.copyClientId(value)}
                        >
                            Скопировать
                        </button>
                    </div>
                ),
            },
            {
                Header: "Статус",
                accessor: "isActive",
                filterable: true,
                filterType: "boolean",
                Filter: (props: FilterProps) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Активен", value: "true" },
                            { label: "Заблокирован", value: "false" },
                        ]}
                    />
                ),
                Cell: ({ value }: { value: boolean }) => (
                    <span className={value ? "text-success" : "text-danger"}>{value ? "Активен" : "Заблокирован"}</span>
                ),
            },
            {
                Header: "Дата создания",
                accessor: "createdAt",
                filterable: true,
                filterType: "dateTimeRange",
                Filter: DateRangeColumnFilter,
                Cell: ({ value }: { value: string }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Последнее использование",
                accessor: "lastUsedAt",
                filterable: true,
                filterType: "dateTimeRange",
                Filter: DateRangeColumnFilter,
                Cell: ({ value }: { value?: string | null }) =>
                    value ? dateTimeToView(value, "DD.MM.YYYY HH:mm:ss") : "—",
            },
            {
                Header: "Действия",
                id: "actions",
                sortable: false,
                notExportable: true,
                width: 330,
                Cell: ({ row }: { row: { original: IntegrationClientListModel } }) => {
                    const item = row.original;
                    return (
                        <div className="d-flex flex-wrap">
                            <Link
                                className="btn btn-xs btn-outline-primary mr-1 mb-1"
                                to={paths.integrationClient(encodeURIComponent(item.clientId))}
                            >
                                Открыть
                            </Link>
                            <button
                                type="button"
                                className={`btn btn-xs mr-1 mb-1 ${item.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                                onClick={() => this.toggleActive(item)}
                            >
                                {item.isActive ? "Заблокировать" : "Активировать"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-xs btn-outline-warning mb-1"
                                onClick={() => this.rotateSecret(item)}
                            >
                                Заменить секрет
                            </button>
                        </div>
                    );
                },
            },
        ];
    }

    copyClientId = (clientId: string) => {
        if (!navigator.clipboard?.writeText) {
            alert({ type: "error", title: "Ошибка", message: "Буфер обмена недоступен." });
            return;
        }
        navigator.clipboard.writeText(clientId).then(
            () => alert({ type: "success", title: "Скопировано", message: "Client ID скопирован." }),
            () => alert({ type: "error", title: "Ошибка", message: "Не удалось скопировать Client ID." }),
        );
    };

    toggleActive = (item: IntegrationClientListModel) => {
        const nextIsActive = !item.isActive;
        this.props.modal({
            name: "confirm",
            title: nextIsActive ? "Активировать интеграционного клиента?" : "Заблокировать интеграционного клиента?",
            text: nextIsActive
                ? "Клиент снова сможет получать токены с текущим Client Secret."
                : "Клиент не сможет получать новые токены. Ранее выданные токены также перестанут приниматься.",
            buttons: { confirm: nextIsActive ? "Активировать" : "Заблокировать", cancel: "Отмена" },
            action: () =>
                applyIntegrationClient({
                    clientId: item.clientId,
                    name: item.name,
                    isActive: nextIsActive,
                    secret: null,
                }).then((result) => !!result),
            onSuccessAction: () => this.props.mergeParams({}),
        });
    };

    rotateSecret = (item: IntegrationClientListModel) => {
        this.props.modal({
            name: "confirm",
            title: "Заменить Client Secret?",
            text: "После замены старый Client Secret нельзя будет использовать для получения новых токенов. Убедитесь, что новый секрет будет передан подрядчику. Уже выданные JWT могут работать до истечения срока.",
            buttons: { confirm: "Заменить", cancel: "Отмена" },
            action: () => {
                let secret: string;
                try {
                    secret = generateIntegrationClientSecret();
                } catch (_error) {
                    alert({
                        type: "error",
                        title: "Ошибка",
                        message: "Браузер не поддерживает безопасную генерацию секрета.",
                    });
                    return Promise.resolve(false);
                }
                return applyIntegrationClient({
                    clientId: item.clientId,
                    name: item.name,
                    isActive: item.isActive,
                    secret,
                }).then((result) => {
                    if (!result) {
                        return false;
                    }
                    return { clientId: item.clientId, name: item.name, secret, title: "Client Secret заменён" };
                });
            },
            onSuccessAction: (
                secretData?: { clientId?: string; name?: string; secret?: string; title?: string } | false,
            ) => {
                if (!secretData || !secretData.clientId || !secretData.name || !secretData.secret) {
                    return;
                }
                this.props.modal({
                    name: "default",
                    title: secretData.title || "Client Secret заменён",
                    windowMod: "wide",
                    CustomBody: SecretModal,
                    bodyData: {
                        clientId: secretData.clientId,
                        clientName: secretData.name,
                        secret: secretData.secret,
                    },
                    withoutEscClose: true,
                    withoutCloseButton: true,
                });
                this.props.mergeParams({});
            },
        });
    };

    renderCreateButton = () => (
        <Link className="btn btn-brand btn-sm" to={paths.integrationClientsNew}>
            Создать интеграционного клиента
        </Link>
    );

    renderListComponent = () => <SharedReactTable {...this.props} columns={this.tableColumns} />;

    render() {
        return (
            <Transition dir="full">
                <div className="page-book__list book-list list book-list--nofilter">
                    <div className="book-list__head-wrap">
                        <div className="book-list__head">
                            <button
                                type="button"
                                aria-label="Закрыть список"
                                className="book-list__head-close close close--sm"
                                onClick={this.close}
                            />
                            <div className="book-list__head-title">Интеграционные клиенты{this.renderCounter()}</div>
                            <div className="btn-toolbar">{this.renderCreateButton()}</div>
                        </div>
                    </div>
                    <div className="container-fluid my-2">{this.renderActions()}</div>
                    {this.renderListComponent()}
                </div>
            </Transition>
        );
    }
}

export default tableContainer(IntegrationClientsList as unknown as FC, "integration-clients");
